import { formatDateWithFormatter } from '@/utils/function'
import { EInvoice, EInvoiceCurrency, EInvoiceItemContract } from '../types'

// Ẩn toolbar + cột thumbnail của viewer PDF (Chrome/Edge: toolbar, navpanes; Firefox: pagemode).
// URL fragment nên không ảnh hưởng link presign / blob
export const PDF_VIEW_PARAMS = '#toolbar=0&navpanes=0&pagemode=none&view=FitH'

// Định dạng theo mục 7 FE_INTEGRATION.md (hoá đơn điện tử): kiểu Việt Nam, `.` ngăn hàng nghìn,
// `,` cho số lẻ. VND không số lẻ (2.921.908), USD 2 số lẻ (541,07 USD).

const numberFormat = (fractionDigits: number) =>
    new Intl.NumberFormat('vi-VN', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    })

export const formatEInvoiceAmount = (
    amount?: number | null,
    currency: EInvoiceCurrency = 'VND'
) => {
    if (amount === undefined || amount === null || isNaN(amount)) return '—'
    return currency === 'USD'
        ? `${numberFormat(2).format(amount)} USD`
        : `${numberFormat(0).format(amount)} VND`
}

/** 26.130 VND/USD (tỷ giá có thể có số lẻ) */
export const formatExchangeRate = (rate?: number | null) =>
    rate
        ? `${new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 4 }).format(rate)} VND/USD`
        : '—'

/** Ngày hoá đơn yyyy-mm-dd → dd/mm/yyyy */
export const formatEInvoiceDate = (value?: string | null) =>
    value ? formatDateWithFormatter(value, 'DD/MM/YYYY') : '—'

/** Câu dẫn chiếu kế toán ghi sau nội dung: "theo Hợp đồng số 131/2026/HDDV-CDN/VNW ký ngày 29/06/2026" */
export const eInvoiceContractReference = (contract: EInvoiceItemContract) =>
    [
        contract.kind === 'annex' ? 'theo Phụ lục số' : 'theo Hợp đồng số',
        contract.number,
        contract.contract_number &&
            `đính kèm Hợp đồng số ${contract.contract_number}`,
        contract.signed_date &&
            `ký ngày ${formatEInvoiceDate(contract.signed_date)}`,
    ]
        .filter(Boolean)
        .join(' ')

/** Thời điểm (UTC ISO 8601) → giờ VN dd/mm/yyyy HH:mm */
export const formatEInvoiceTime = (value?: string | null) =>
    value
        ? formatDateWithFormatter(value, 'DD/MM/YYYY HH:mm', { fromUTC: true })
        : '—'

/** Trạng thái hiển thị (badge) gộp status + tax_status + đã xác nhận sai */
export type EInvoiceBadge =
    | 'not-issued'
    | 'publishing'
    | 'waiting-tax'
    | 'granted'
    | 'tax-rejected'
    | 'tax-error'
    | 'failed'
    | 'marked-wrong'

export const getEInvoiceBadge = (eInvoice?: EInvoice | null): EInvoiceBadge => {
    if (!eInvoice) return 'not-issued'
    if (eInvoice.status === 'publishing') return 'publishing'
    if (eInvoice.status === 'failed') return 'failed'
    // published
    if (eInvoice.marked_wrong_at) return 'marked-wrong'
    switch (eInvoice.tax_status) {
        case 'granted':
            return 'granted'
        case 'rejected':
            return 'tax-rejected'
        case 'error':
            return 'tax-error'
        default:
            return 'waiting-tax'
    }
}

export const E_INVOICE_BADGE_STYLES: Record<EInvoiceBadge, string> = {
    'not-issued': 'bg-gray-100 text-gray-700',
    publishing: 'bg-amber-50 text-amber-800',
    'waiting-tax': 'bg-blue-50 text-blue-800',
    granted: 'bg-green-50 text-green-800',
    'tax-rejected': 'bg-red-50 text-red-800',
    'tax-error': 'bg-red-50 text-red-800',
    failed: 'bg-red-50 text-red-800',
    'marked-wrong': 'bg-red-50 text-red-800',
}
